import React, { useState, useCallback, useMemo } from "react";
import { Box, Typography, Button, Alert } from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import {
  useViewQuery,
  useViewDataQuery,
  useWorkspaceObjectChangeQueryInvalidation,
  useCreateRowMutation,
  useUpdateRowMutation,
  useDeleteRowMutation,
} from "../hooks/useViewDataQuery";
import { ViewGridNew, ViewGridSkeleton } from "../components/viewGrid";
import ViewGridErrorBoundary from "../components/viewGrid/ViewGridErrorBoundary";
import type { ColumnFilter } from "../components/viewGrid/serverFilters";

type FocusTableKey = { primary_key_column?: string };

/**
 * ViewViewer - Displays view data in MUI X DataGrid
 *
 * Uses React Query for data fetching with caching and optimistic updates.
 * Uses MUI X DataGrid for cell-level editing and server-side pagination.
 */
export default function ViewViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const viewId = id || null;
  const focusedRowId = searchParams.get("row");

  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  useWorkspaceObjectChangeQueryInvalidation(workspaceId);
  const authFetch = useAuthFetch();

  // Pagination and sorting state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [sort, setSort] = useState<
    { field: string; direction: "ASC" | "DESC" }[]
  >([]);

  // Fetch view definition using React Query
  const {
    data: view,
    isLoading: viewLoading,
    error: viewError,
  } = useViewQuery(viewId);

  const {
    data: focusTable,
    isLoading: focusTableLoading,
    error: focusTableError,
  } = useQuery<FocusTableKey>({
    queryKey: ["myvault", "focus-table", workspaceId, view?.focus_table_id],
    queryFn: async () => {
      const table = (await authFetch(
        `/api/workspace/myvault/schemas/tables/${view!.focus_table_id}`,
        "fetch view focus table",
        { headers: { "x-workspace-id": workspaceId } },
      )) as FocusTableKey;
      if (!table.primary_key_column) {
        throw new Error(
          "This view has no primary key for the selected record.",
        );
      }
      return table;
    },
    enabled: !!focusedRowId && !!view?.focus_table_id && !!workspaceId,
    staleTime: 30_000,
  });
  const primaryKeyColumn = focusedRowId ? focusTable?.primary_key_column : null;
  const filters = useMemo(
    () => [
      ...(focusedRowId && primaryKeyColumn
        ? [
            {
              field: primaryKeyColumn,
              operator: "eq" as const,
              value: focusedRowId,
            },
          ]
        : []),
      ...columnFilters,
    ],
    [focusedRowId, primaryKeyColumn, columnFilters],
  );

  // Fetch view data with pagination and sorting using React Query
  const {
    data: viewData,
    isLoading: dataLoading,
    error: dataError,
    refetch: refreshData,
  } = useViewDataQuery(!focusedRowId || primaryKeyColumn ? viewId : null, {
    page: page + 1, // API expects 1-based page numbers
    pageSize,
    filters: filters.length ? filters : undefined,
    sort: sort.length > 0 ? sort : undefined,
  });

  // Row mutations with React Query
  const createRowMutation = useCreateRowMutation();
  const updateRowMutation = useUpdateRowMutation();
  const deleteRowMutation = useDeleteRowMutation();

  // Handle sort change
  const handleSort = useCallback((field: string, direction: "ASC" | "DESC") => {
    setSort([{ field, direction }]);
    setPage(0); // Reset to first page on sort change
  }, []);

  // Server-side column filters apply before pagination.
  const handleColumnFilterChange = useCallback((next: ColumnFilter[]) => {
    setColumnFilters(next);
    setPage(0);
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page on page size change
  }, []);

  // Handle row create
  const handleRowCreate = useCallback(
    async (data: Record<string, unknown>) => {
      if (!viewId) return;
      await createRowMutation.mutateAsync({
        viewId,
        data,
      });
    },
    [viewId, createRowMutation],
  );

  // Handle row update
  const handleRowUpdate = useCallback(
    async (rowId: string, data: Record<string, unknown>) => {
      if (!viewId) return;
      await updateRowMutation.mutateAsync({
        viewId,
        rowId,
        data,
      });
    },
    [viewId, updateRowMutation],
  );

  // Handle row delete
  const handleRowDelete = useCallback(
    async (rowId: string) => {
      if (!viewId) return;
      await deleteRowMutation.mutateAsync({
        viewId,
        rowId,
      });
    },
    [viewId, deleteRowMutation],
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refreshData();
  }, [refreshData]);

  // Navigation handlers
  const handleBack = () => {
    const returnTo = (location.state as { canvasReturnTo?: string } | null)
      ?.canvasReturnTo;
    navigate(
      returnTo?.startsWith(`/workspace/${workspaceId}/myvault/`)
        ? returnTo
        : `/workspace/${workspaceId}/myvault/views`,
    );
  };

  const handleEdit = () => {
    navigate(`/workspace/${workspaceId}/myvault/views/${viewId}/edit`);
  };

  // Loading and error states
  const loading =
    viewLoading || focusTableLoading || (dataLoading && !viewData);
  const error = viewError || focusTableError || dataError;
  const errorMessage =
    error instanceof Error ? error.message : error ? String(error) : null;

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <ViewGridSkeleton rows={10} columns={6} />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{errorMessage}</Alert>
        <Button variant="plain" onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!view) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">View not found.</Alert>
        <Button variant="plain" onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.level1" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: "background.surface",
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button
          variant="plain"
          color="neutral"
          startDecorator={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography level="h4">{view.name}</Typography>
          {view.description && (
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              {view.description}
            </Typography>
          )}
        </Box>
        <Button
          variant="plain"
          color="neutral"
          startDecorator={<RefreshIcon />}
          onClick={handleRefresh}
          loading={dataLoading}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          color="neutral"
          startDecorator={<EditIcon />}
          onClick={handleEdit}
        >
          Edit View
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {(() => {
          // Skip 409 conflict errors -- handled by conflict resolution dialog
          const updateErr =
            updateRowMutation.error &&
            (updateRowMutation.error as any)?.status !== 409
              ? updateRowMutation.error
              : null;
          const deleteErr =
            deleteRowMutation.error &&
            (deleteRowMutation.error as any)?.status !== 409
              ? deleteRowMutation.error
              : null;
          const err = createRowMutation.error || updateErr || deleteErr;
          if (!err) return null;
          return (
            <Alert color="danger" sx={{ mb: 2 }}>
              {err instanceof Error ? err.message : "An error occurred"}
            </Alert>
          );
        })()}

        <ViewGridErrorBoundary onReset={handleRefresh}>
          <ViewGridNew
            view={view}
            data={viewData || null}
            loading={
              dataLoading ||
              createRowMutation.isPending ||
              updateRowMutation.isPending ||
              deleteRowMutation.isPending
            }
            error={null}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSort={handleSort}
            onColumnFilterChange={handleColumnFilterChange}
            onRowCreate={handleRowCreate}
            onRowUpdate={handleRowUpdate}
            onRowDelete={handleRowDelete}
            onRefresh={handleRefresh}
          />
        </ViewGridErrorBoundary>
      </Box>
    </Box>
  );
}
